"use client"

import { FileCode, FileType, BookOpen, Terminal, ImageIcon, RotateCcw } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useLayoutPanels } from "@/components/editor/dock/layout-context"
import {
  PANEL_COMPONENTS,
  PANEL_LABELS,
  type PanelComponent,
} from "@/components/editor/dock/layout-model"

const PANEL_ICONS: Record<PanelComponent, typeof FileCode> = {
  [PANEL_COMPONENTS.CODE_EDITOR]: FileCode,
  [PANEL_COMPONENTS.CONSOLE]: Terminal,
  [PANEL_COMPONENTS.PDF_PREVIEW]: FileType,
  [PANEL_COMPONENTS.EPUB_PREVIEW]: BookOpen,
  [PANEL_COMPONENTS.IMAGE]: ImageIcon,
}

export function ViewMenu() {
  const { closedPanels, reopenPanel, resetLayout } = useLayoutPanels()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-[22px] items-center gap-1.5 rounded-[4px] px-2.5",
            "border border-[var(--ed-ribbon-field-border)] bg-[var(--ed-ribbon-field-bg)]",
            "text-xs font-normal text-[var(--ed-ribbon-text)]",
            "hover:bg-[var(--ed-ribbon-field-hover)]",
            "outline-none",
          )}
        >
          <img
            src="/viewIcon.svg"
            alt=""
            draggable={false}
            style={{ filter: "var(--ed-icon-filter-mono, none)" }}
            className="h-3 w-[14px] opacity-80"
          />
          View
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[200px]">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Closed panels
        </DropdownMenuLabel>

        {closedPanels.length === 0 ? (
          <DropdownMenuItem disabled className="text-xs text-muted-foreground">
            All panels are open
          </DropdownMenuItem>
        ) : (
          closedPanels.map((panel) => {
            const Icon = PANEL_ICONS[panel]
            return (
              <DropdownMenuItem
                key={panel}
                className="text-xs cursor-pointer"
                onClick={() => reopenPanel(panel)}
              >
                <Icon className="h-3.5 w-3.5" />
                Show {PANEL_LABELS[panel]}
              </DropdownMenuItem>
            )
          })
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="text-xs cursor-pointer"
          onClick={resetLayout}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to Default Layout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
